import {Observable} from 'rxjs/Observable';
import 'rxjs/add/operator/toPromise';
import React from 'react';
import ReactDOM from 'react-dom';
import {Provider} from 'react-redux';
import {ModalStack} from './ModalStack';
import {ModalManagerComponent} from './ModalManagerComponent';

export class ModalManager {
  constructor(config, store) {
    /** @private */
    this.config = config || {};
    this.modalStack = new ModalStack();
    /** @private */
    this.updateState = () => null;
    this.handleSetComponent = this.handleSetComponent.bind(this);

    this.node = document.createElement('div');
    document.body.appendChild(this.node);
    ReactDOM.render((
      <Provider store={store}>
        <ModalManagerComponent
          onInit={this.handleSetComponent}
        />
      </Provider>
    ), this.node);
  }

  /**
   * Register the manager component once it is mounted.
   * @param component
   * @private
   */
  handleSetComponent(component) {
    /** @private */
    this.updateState = component.setState.bind(component);
  }

  /**
   * Create a modal observable.
   * Subscribe to open it.
   * Unsubscribe to close it.
   * The component will also be injected with props onSuccess and onCancel which with emit next(arg) and error(arg) respectively, followed by completing the observable.
   * @param component
   * @param {ModalOptions} config
   * @return {Observable}
   */
  create(component, config) {
    return Observable.create(observer => {
      const key = Date.now();
      const modal = {
        key,
        component,
        config,
        onSuccess: (result) => (observer.next(result), observer.complete()),
        onCancel: (reason) => (observer.error(reason), observer.complete()),
      };

      if (this.config.onOpen)
        this.config.onOpen(this, modal);

      this.modalStack.add(modal);
      this.updateState({
        modals: this.modalStack.stack,
      });

      return () => {
        this.close(key);
        if (this.config.onClose) {
          this.config.onClose(this);
        }
      };
    });
  }

  /**
   * Create and immediately open a modal.
   * @param component
   * @param {ModalOptions} config
   * @return {Promise}
   */
  open(component, config) {
    return this.create(component, config).toPromise();
  }

  /**
   * Used intermally to remove the modal from the view.
   * Normally called from within the modal or unsubscribing from it.
   * @param key
   * @private
   */
  close(key) {
    this.modalStack.remove(key);
    this.updateState({
      modals: this.modalStack.stack,
    });
  }

  /**
   * Forcefully close all open modals.
   */
  closeAll() {
    this.modalStack.stack.forEach(modal => modal.onCancel());
  }

  /**
   * Forcefully close all open modals.
   */
  closeAllOnNav() {
    this.modalStack.stack.forEach(modal => !modal.config.keepOpenOnNav && modal.onCancel());
  }
}

/**
 * @typedef {Object} ModalOptions
 * @property {Function} [onOpen]
 * @property {Function} [onClose]
 * @property {Boolean} [isLegacy]
 * @property {Boolean} [backdropClickToClose=true]
 * @property {Boolean} [hardwareBackButtonClose=true]
 * @property {Boolean} [keepOpenOnNav=false]
 * @property {Object} [modalProps]
 */



// WEBPACK FOOTER //
// ./src/js/core-utilities/react-modals/ModalManager.js